import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Building2, Briefcase, Sparkles, Calendar, Check } from "lucide-react";

function formatMemberSince(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setDepartment(user.department || "");
      setRoleTitle(user.role_title || "");
      setDirty(false);
    }
  }, [user]);

  if (!user) return null;

  const onChange = (setter) => (e) => {
    setter(e.target.value);
    setDirty(true);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        name: name.trim(),
        department: department.trim(),
        role_title: roleTitle.trim(),
      });
      toast.success("Profile updated");
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-10 fade-in">
      <div className="mb-8">
        <div className="label-eyebrow mb-2">Account</div>
        <h1 className="font-display text-[32px] md:text-[38px] font-medium tracking-tight text-[#0B1736]">
          Your profile
        </h1>
        <p className="text-[13.5px] text-[#5F6677] mt-1">
          Update how you appear on proposals and inside the workspace.
        </p>
      </div>

      {/* Identity card */}
      <div data-testid="profile-identity" className="quo-card p-6 md:p-7 flex items-center gap-5 mb-6">
        <div
          data-testid="profile-avatar"
          className="h-16 w-16 rounded-full grid place-items-center text-white font-semibold text-[20px] shrink-0"
          style={{ background: "#011B90", boxShadow: "0 12px 32px -12px rgba(1, 27, 144,0.55)" }}
        >
          {user.avatar_initials || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[20px] font-medium tracking-tight text-[#0B1736] truncate">
            {user.name}
          </div>
          <div className="text-[13px] text-[#5F6677] truncate flex items-center gap-1.5">
            <Mail size={12} className="text-[#9098A8]" /> {user.email}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: "#EDEFF7", color: "#011670" }}
            >
              <Sparkles size={10} /> {user.plan}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#F1F2F5] text-[#5F6677]">
              <Calendar size={10} /> Since {formatMemberSince(user.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Editable fields */}
      <div className="quo-card p-6 md:p-7">
        <h2 className="font-display text-[17px] font-medium text-[#0B1736]">Details</h2>
        <p className="text-[12.5px] text-[#9098A8] mt-0.5">
          Changes here update your proposal signature.
        </p>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-[#9098A8]">
              Name
            </Label>
            <Input
              data-testid="profile-name"
              value={name}
              onChange={onChange(setName)}
              className="mt-1.5 h-10 bg-white border-[#E8EAF3] focus-visible:ring-[#011B90]/30 focus-visible:border-[#011B90]"
            />
          </div>

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-[#9098A8]">
              Email
            </Label>
            <Input
              data-testid="profile-email"
              value={user.email}
              readOnly
              className="mt-1.5 h-10 bg-[#FAFBFF] border-[#E8EAF3] text-[#5F6677] cursor-not-allowed"
            />
          </div>

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-[#9098A8]">
              <span className="inline-flex items-center gap-1"><Briefcase size={11} /> Role</span>
            </Label>
            <Input
              data-testid="profile-role"
              value={roleTitle}
              onChange={onChange(setRoleTitle)}
              placeholder="Senior Account Executive"
              className="mt-1.5 h-10 bg-white border-[#E8EAF3] focus-visible:ring-[#011B90]/30 focus-visible:border-[#011B90]"
            />
          </div>

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-[#9098A8]">
              <span className="inline-flex items-center gap-1"><Building2 size={11} /> Department</span>
            </Label>
            <Input
              data-testid="profile-department"
              value={department}
              onChange={onChange(setDepartment)}
              placeholder="Sales"
              className="mt-1.5 h-10 bg-white border-[#E8EAF3] focus-visible:ring-[#011B90]/30 focus-visible:border-[#011B90]"
            />
          </div>

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-[#9098A8]">
              Plan
            </Label>
            <Input
              data-testid="profile-plan"
              value={user.plan || "—"}
              readOnly
              className="mt-1.5 h-10 bg-[#FAFBFF] border-[#E8EAF3] text-[#5F6677] cursor-not-allowed"
            />
          </div>

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-[#9098A8]">
              Member since
            </Label>
            <Input
              data-testid="profile-member-since"
              value={formatMemberSince(user.created_at)}
              readOnly
              className="mt-1.5 h-10 bg-[#FAFBFF] border-[#E8EAF3] text-[#5F6677] cursor-not-allowed"
            />
          </div>
        </div>

        <div className="mt-7 flex items-center justify-end gap-3">
          <span className={`text-[12px] font-medium ${dirty ? "text-[#011B90]" : "text-[#9098A8]"}`}>
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <Button
            data-testid="profile-save"
            onClick={onSave}
            disabled={!dirty || saving}
            className="h-10 px-4 bg-[#011B90] hover:bg-[#011670] text-white rounded-md disabled:opacity-60 flex items-center gap-2"
          >
            <Check size={15} />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
